<template>
  <el-drawer
    :visible.sync="localVisible"
    direction="rtl"
    size="100%"
    :with-header="false"
    :before-close="handleClose"
    class="crm-drawer"
    custom-class="fullscreen-content-drawer"
    append-to-body
  >
    <div v-if="clientData" class="crm-drawer-inner">
      <!-- Header -->
      <div class="crm-header">
        <div class="crm-header-left">
          <div class="crm-avatar" :style="{ background: avatarBg }">
            {{ initials(clientData.name) }}
          </div>
          <div class="crm-header-info">
            <span class="crm-name">{{ clientData.name }}</span>
            <span :class="['crm-status-badge', clientData.status]">
              <i :class="statusIcon(clientData.status)"></i>
              {{ statusLabel(clientData.status) }}
            </span>
          </div>
        </div>
        <button class="crm-close-btn" @click="handleClose">
          <i class="el-icon-close"></i>
        </button>
      </div>

      <!-- Body scrollable -->
      <div class="crm-body">
        <!-- Sección Resumen -->
        <div class="crm-section">
          <div class="crm-section-title">Resumen</div>
          <div class="crm-stats-grid">
            <div class="crm-stat">
              <span class="crm-stat-value">
                {{
                  clientData.days_since_last_invoice !== null &&
                  clientData.days_since_last_invoice !== undefined
                    ? clientData.days_since_last_invoice + " días"
                    : "Sin datos"
                }}
              </span>
              <span class="crm-stat-label">Último contacto</span>
            </div>
            <div class="crm-stat">
              <span class="crm-stat-value">{{
                clientData.total_quotes || 0
              }}</span>
              <span class="crm-stat-label">Cotizaciones</span>
            </div>
            <div class="crm-stat">
              <span class="crm-stat-value">{{
                clientData.converted_quotes || 0
              }}</span>
              <span class="crm-stat-label">Convertidas</span>
            </div>
          </div>
          <div class="crm-score-row">
            <span class="crm-score-label">Score de conversión</span>
            <span class="crm-score-value"
              >{{ clientData.conversion_score || 0 }}%</span
            >
          </div>
          <div class="crm-score-bar-track">
            <div
              class="crm-score-bar"
              :style="{
                width: (clientData.conversion_score || 0) + '%',
                background: scoreBarColor(clientData.conversion_score)
              }"
            ></div>
          </div>
        </div>

        <!-- Sección Cambiar estado -->
        <div class="crm-section">
          <div class="crm-section-title">Cambiar estado</div>
          <div class="crm-status-btns">
            <button
              v-for="s in statusOptions"
              :key="s.value"
              :class="[
                'crm-status-btn',
                s.value,
                { active: localStatus === s.value }
              ]"
              @click="changeStatus(s.value)"
            >
              <i :class="s.icon"></i> {{ s.label }}
            </button>
          </div>
        </div>

        <!-- Sección Nota rápida -->
        <div class="crm-section">
          <div class="crm-section-title">Nota rápida</div>
          <el-input
            v-model="quickNote"
            type="textarea"
            :rows="3"
            placeholder="Escribe una nota sobre este cliente..."
            :maxlength="300"
            show-word-limit
          />
          <el-button
            size="small"
            type="primary"
            plain
            class="crm-save-note-btn"
            :disabled="!quickNote.trim()"
            @click="saveNote"
          >
            Guardar nota
          </el-button>
          <div v-if="savedNote" class="crm-saved-note">
            <i class="el-icon-info"></i>
            <span>{{ savedNote }}</span>
          </div>
        </div>

        <!-- Sección Acciones -->
        <div class="crm-section">
          <div class="crm-section-title">Acciones</div>
          <div class="crm-action-btns">
            <el-button
              size="small"
              class="crm-action-full"
              @click="sendWhatsApp"
            >
              <i class="el-icon-chat-round"></i> Enviar WhatsApp
            </el-button>
            <el-button size="small" class="crm-action-full" @click="sendEmail">
              <i class="el-icon-message"></i> Enviar Email
            </el-button>
            <el-button
              size="small"
              type="text"
              class="crm-action-link"
              @click="goToProfile"
            >
              Ver perfil completo <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </div>
      </div>

      <!-- Footer sticky -->
      <div class="crm-footer">
        <el-button
          size="small"
          type="primary"
          class="crm-footer-btn"
          @click="goToSales"
        >
          Ver en Ventas
        </el-button>
      </div>
    </div>

    <!-- Fallback si no hay datos -->
    <div v-else class="crm-empty-state">
      <i class="el-icon-user"></i>
      <span>Sin datos del cliente</span>
    </div>
  </el-drawer>
</template>

<script>
const STATUS_MAP = {
  hot: { label: "Caliente", icon: "el-icon-top", color: "#1d9e75" },
  warm: { label: "Tibio", icon: "el-icon-sort", color: "#3fdbbf" },
  cold: { label: "Frío", icon: "el-icon-bottom", color: "#ba7517" },
  dormant: { label: "Dormido", icon: "el-icon-time", color: "#e24b4a" },
  new: { label: "Nuevo", icon: "el-icon-circle-plus-outline", color: "#534ab7" }
};

const AVATAR_COLORS = ["#31355c", "#534ab7", "#3fdbbf", "#1d9e75", "#ba7517"];

const NOTE_KEY_PREFIX = "crm_note_";

export default {
  name: "ClientCRMDrawer",
  props: {
    visible: { type: Boolean, default: false },
    clientData: { type: Object, default: null }
  },
  emits: ["update:visible", "status-changed", "send-message"],
  data() {
    return {
      localStatus: null,
      quickNote: "",
      savedNote: null
    };
  },
  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      }
    },
    drawerSize() {
      return window.innerWidth < 768 ? "100%" : "380px";
    },
    avatarBg() {
      if (!this.clientData) return AVATAR_COLORS[0];
      const idx =
        (this.clientData.name || "").charCodeAt(0) % AVATAR_COLORS.length;
      return AVATAR_COLORS[idx];
    },
    statusOptions() {
      return Object.entries(STATUS_MAP).map(([value, s]) => ({
        value,
        label: s.label,
        icon: s.icon
      }));
    }
  },
  watch: {
    clientData(val) {
      if (val) {
        this.localStatus = val.status || null;
        this.loadNote();
      }
    }
  },
  methods: {
    initials(name) {
      if (!name) return "?";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    statusLabel(status) {
      return (STATUS_MAP[status] || {}).label || status;
    },
    statusIcon(status) {
      return (STATUS_MAP[status] || {}).icon || "el-icon-question";
    },
    scoreBarColor(score) {
      if (!score) return "#c0c4cc";
      if (score >= 70) return "#1d9e75";
      if (score >= 40) return "#3fdbbf";
      return "#ba7517";
    },
    changeStatus(newStatus) {
      this.localStatus = newStatus;
      this.$emit("status-changed", {
        clientId: this.clientData.id,
        newStatus,
        client: { ...this.clientData, status: newStatus }
      });
      this.$message.success(
        `Estado de ${this.clientData.name} cambiado a ${STATUS_MAP[newStatus]
          ?.label || newStatus}`
      );
    },
    loadNote() {
      if (!this.clientData) return;
      try {
        this.savedNote =
          localStorage.getItem(NOTE_KEY_PREFIX + this.clientData.id) || null;
      } catch {
        this.savedNote = null;
      }
    },
    saveNote() {
      if (!this.clientData || !this.quickNote.trim()) return;
      try {
        localStorage.setItem(
          NOTE_KEY_PREFIX + this.clientData.id,
          this.quickNote.trim()
        );
        this.savedNote = this.quickNote.trim();
        this.quickNote = "";
        this.$message.success("Nota guardada");
      } catch {
        this.$message.error("Error al guardar la nota");
      }
    },
    sendWhatsApp() {
      this.$emit("send-message", {
        recipients: [
          {
            id: this.clientData.id,
            name: this.clientData.name,
            phone: this.clientData.phone || null,
            email: this.clientData.email || null
          }
        ],
        context: "clients",
        channel: "whatsapp"
      });
    },
    sendEmail() {
      this.$emit("send-message", {
        recipients: [
          {
            id: this.clientData.id,
            name: this.clientData.name,
            phone: this.clientData.phone || null,
            email: this.clientData.email || null
          }
        ],
        context: "clients",
        channel: "email"
      });
    },
    goToProfile() {
      if (this.clientData && this.clientData.id) {
        this.$router.push(`/dashboard/directorio/${this.clientData.id}`);
      }
      this.handleClose();
    },
    goToSales() {
      this.$router.push("/dashboard/ventas");
      this.handleClose();
    },
    handleClose() {
      this.$emit("update:visible", false);
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.crm-drawer-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

// Header
.crm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
}
.crm-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.crm-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}
.crm-header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.crm-name {
  font-size: 15px;
  font-weight: 700;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}
.crm-status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  width: fit-content;
  &.hot {
    background: #e4fff4;
    color: #1d9e75;
  }
  &.warm {
    background: #e0fcf4;
    color: #0d9488;
  }
  &.cold {
    background: #fff8e6;
    color: #ba7517;
  }
  &.dormant {
    background: #fff0f0;
    color: #e24b4a;
  }
  &.new {
    background: #ece8ff;
    color: #534ab7;
  }
}
.crm-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #909399;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.15s;
  flex-shrink: 0;
  &:hover {
    color: $primary;
  }
}

// Body
.crm-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.crm-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.crm-section-title {
  font-size: 11px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid $border;
  padding-bottom: 6px;
}

// Stats
.crm-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.crm-stat {
  background: $bg;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.crm-stat-value {
  font-size: 16px;
  font-weight: 700;
  color: $primary;
}
.crm-stat-label {
  font-size: 10px;
  color: #909399;
  text-align: center;
}

// Score bar
.crm-score-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.crm-score-label {
  font-size: 12px;
  color: #606266;
}
.crm-score-value {
  font-size: 13px;
  font-weight: 700;
  color: $primary;
}
.crm-score-bar-track {
  height: 8px;
  background: #eef0f5;
  border-radius: 4px;
  overflow: hidden;
}
.crm-score-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
  min-width: 2px;
}

// Status buttons
.crm-status-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.crm-status-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid $border;
  border-radius: 20px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #606266;
  transition: all 0.15s;
  &:hover {
    border-color: $primary;
    color: $primary;
  }
  &.active {
    border-color: $primary;
    background: $primary;
    color: #fff;
    font-weight: 600;
  }
  &.hot.active {
    background: #1d9e75;
    border-color: #1d9e75;
  }
  &.warm.active {
    background: #0d9488;
    border-color: #0d9488;
  }
  &.cold.active {
    background: #ba7517;
    border-color: #ba7517;
  }
  &.dormant.active {
    background: #e24b4a;
    border-color: #e24b4a;
  }
  &.new.active {
    background: #534ab7;
    border-color: #534ab7;
  }
}

// Note
.crm-save-note-btn {
  align-self: flex-end;
}
.crm-saved-note {
  background: $bg;
  border: 1px solid $border;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  gap: 6px;
  align-items: flex-start;
  font-size: 12px;
  color: #606266;
  i {
    color: $accent;
    flex-shrink: 0;
    margin-top: 1px;
  }
}

// Action buttons
.crm-action-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.crm-action-full {
  flex: 1;
  min-width: 0;
  justify-content: center;
}
.crm-action-link {
  width: 100%;
  text-align: left;
  padding: 0;
  color: $primary;
  font-size: 13px;
}

// Footer
.crm-footer {
  padding: 14px 20px;
  border-top: 1px solid $border;
  flex-shrink: 0;
}
.crm-footer-btn {
  width: 100%;
}

// Empty
.crm-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 10px;
  color: #c0c4cc;
  i {
    font-size: 40px;
  }
  span {
    font-size: 14px;
  }
}

// Mobile full screen
@media (max-width: 768px) {
  .crm-name {
    max-width: 180px;
  }
}
</style>
